import React, {
  useRef,
  useState,
  useImperativeHandle,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { Form } from '@unform/web';
import axios from 'axios';
import { isFunction, isArray } from 'lodash';
import PropTypes from 'prop-types';

import { t } from '~/helpers';
import api from '~/services/api';

import { Container } from './styles';

async function getErrorMessage(err) {
  if (!err) {
    return 'Erro ao emitir relatório.';
  }

  if (typeof err === 'string') {
    return t(err);
  }

  if (err instanceof Error && err.message) {
    return t(err.message);
  }

  const responseData = err?.response?.data;

  if (responseData instanceof Blob) {
    try {
      const blobText = await responseData.text();

      if (!blobText) {
        return 'Erro ao emitir relatório.';
      }

      try {
        const parsed = JSON.parse(blobText);

        return (
          parsed?.message ||
          parsed?.mensagem ||
          parsed?.error ||
          parsed?.erro ||
          t(blobText)
        );
      } catch (parseError) {
        return t(blobText);
      }
    } catch (blobError) {
      return 'Erro ao emitir relatório.';
    }
  }

  if (typeof responseData === 'string') {
    return t(responseData);
  }

  if (responseData && typeof responseData === 'object') {
    return (
      responseData.message ||
      responseData.mensagem ||
      responseData.error ||
      responseData.erro ||
      'Erro ao emitir relatório.'
    );
  }

  return 'Erro ao emitir relatório.';
}

const Report = React.forwardRef((props, ref) => {
  const {
    open,
    onClose,
    title,
    children,
    confirm,
    cancel,
    relatorio,
    serialize,
    initials,
    validate,
    maxWidth,
    requestType,
  } = props;

  const formRef = useRef(null);
  const mobile = window.innerWidth <= 768;

  const { token } = api.defaults.headers;

  const [loading, setLoading] = useState(false);

  const openWithHeaders = (url, headers) => {
    const params = new URLSearchParams(url.split('?')[1]);

    // Cria um formulário dinâmico
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = url.split('?')[0];
    form.target = '_blank'; // Abre em uma nova janela/aba

    params.forEach((value, key) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    });

    // Adiciona os headers como campos ocultos
    for (const [key, value] of Object.entries(headers)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }

    // Adiciona o formulário ao DOM, envia e remove
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  async function handleSubmit(param) {
    try {
      setLoading(true);

      formRef.current.setErrors({});

      const REACT_APP_URL_DOWNLOAD = sessionStorage.getItem('URL_REPORT')
        ? sessionStorage.getItem('URL_REPORT')
        : process.env.REACT_APP_URL_DOWNLOAD;

      await validate(param);

      const forms = isFunction(serialize) ? serialize(param) : param;

      const url = new URL(REACT_APP_URL_DOWNLOAD);
      url.search = new URLSearchParams({
        relatorio,
        //        token, // Pode ser omitido se o header já vai incluir o token
        ...JSON.parse(JSON.stringify(forms)),
      });

      const response = async () => {
        if (requestType === 'POST' || requestType === 'PUT') {
          const url = new URL(REACT_APP_URL_DOWNLOAD);
          url.search = new URLSearchParams({
            relatorio,
            tipo: forms.formato ? forms.formato : forms.tipo,
          });

          const res = await axios({
            url,
            method: requestType,
            responseType: 'blob',
            headers: {
              Authorization: `Bearer ${token}`,
            },
            data: {
              ...forms,
            },
          });

          return res;
        }

        const res = await axios({
          url: url.href,
          method: requestType,
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        return res;
      };

      const resp = await response();

      if (resp.config.method.toLowerCase() === 'get') {
        const url = resp.config.url.href ?? resp.config.url;
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        openWithHeaders(url, headers);
      } else {
        const file = new Blob([resp.data], { type: resp.data.type });

        const a = document.createElement('a');
        const objectUrl = window.URL.createObjectURL(file);
        a.href = objectUrl;
        a.download = resp.data.type.includes('pdf') ? title : `${title}.xls`;
        a.click();
      }
    } catch (err) {
      if (err.form) {
        formRef.current.setErrors(err.form);
      } else if (isArray(err)) {
        err.map(async (e) => toast.error(await getErrorMessage(e)));
      } else {
        toast.error(await getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  }

  const clearField = useCallback((field) => {
    formRef.current.clearField(field);
  }, []);

  const getData = useCallback(() => {
    formRef.current.getData();
  }, []);

  const reset = useCallback(() => {
    formRef.current.reset();
  }, []);

  const setData = useCallback((data) => {
    formRef.current.setData(data);
  }, []);

  const setFieldValue = useCallback((field, value) => {
    formRef.current.setFieldValue(field, value);
  }, []);

  const setErrors = useCallback((data) => {
    formRef.current.setErrors(data);
  }, []);

  const setFieldError = useCallback((field, value) => {
    formRef.current.setFieldError(field, value);
  }, []);

  useImperativeHandle(ref, () => ({
    clearField,
    getData,
    reset,
    setData,
    setFieldValue,
    setErrors,
    setFieldError,
  }));

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      fullScreen={mobile}
    >
      <DialogTitle>
        <Typography variant="h6">{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Form ref={formRef} onSubmit={handleSubmit} initialData={initials}>
          <Container>{children}</Container>
        </Form>
      </DialogContent>
      <DialogActions>
        {loading ? (
          <CircularProgress
            size={20}
            color="primary"
            style={{ marginRight: '1.5rem' }}
          />
        ) : (
          <Button
            loading
            onClick={() => formRef.current.submitForm()}
            color="primary"
            variant="outlined"
          >
            {mobile ? 'Download' : confirm}
          </Button>
        )}

        <Button onClick={() => onClose()} color="secondary" variant="outlined">
          {cancel}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

Report.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  children: PropTypes.element.isRequired,
  confirm: PropTypes.string,
  cancel: PropTypes.string,
  relatorio: PropTypes.string.isRequired,
  serialize: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  initials: PropTypes.objectOf(PropTypes.any),
  validate: PropTypes.func,
  maxWidth: PropTypes.string,
  requestType: PropTypes.oneOfType(['GET', 'POST', 'PUT', 'DELETE', 'UPDATE']),
};

Report.defaultProps = {
  open: false,
  title: 'Abastek',
  confirm: 'Exibir',
  cancel: 'Cancelar',
  serialize: false,
  initials: {},
  validate: async () => {},
  maxWidth: 'sm',
  requestType: 'GET',
};

export default Report;
